import React from "react";
import { Checkbox } from "../../../../ui/components/Checkbox";

interface Module {
  id: number;
  module_code: string;
  module_name: string;
  actions: Array<{
    id: number;
    action_code: string;
    action_name: string;
  }>;
}

interface RolePermissionMatrixProps {
  modules: Module[];
  permissions: Record<string, Record<string, boolean>>; // module_code -> action_code -> is_granted
  reviewPolicies: Record<string, Record<string, boolean>>; // module_code -> action_code -> requires_review
  isManagerRequired: boolean;
  onPermissionChange: (
    moduleCode: string,
    actionCode: string,
    granted: boolean
  ) => void;
  onReviewPolicyChange: (
    moduleCode: string,
    actionCode: string,
    requiresReview: boolean
  ) => void;
  readOnly?: boolean;
}

export const RolePermissionMatrix: React.FC<RolePermissionMatrixProps> = ({
  modules,
  permissions,
  reviewPolicies,
  isManagerRequired,
  onPermissionChange,
  onReviewPolicyChange,
  readOnly = false,
}) => {
  const handleModuleSelectAll = (moduleCode: string, grant: boolean) => {
    if (readOnly) return;
    const module = modules.find((m) => m.module_code === moduleCode);
    if (!module) return;

    module.actions.forEach((action) => {
      onPermissionChange(moduleCode, action.action_code, grant);
    });
  };

  const getPermission = (moduleCode: string, actionCode: string): boolean => {
    return permissions[moduleCode]?.[actionCode] ?? false;
  };

  const getReviewPolicy = (moduleCode: string, actionCode: string): boolean => {
    return reviewPolicies[moduleCode]?.[actionCode] ?? false;
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {modules.map((module) => (
        <div
          key={module.module_code}
          className="flex flex-col gap-3 border border-solid border-neutral-border rounded-lg p-4 bg-default-background"
        >
          {/* Module Header */}
          <div className="flex items-center justify-between border-b border-solid border-neutral-border pb-2">
            <span className="text-heading-3 font-heading-3 text-default-font">
              {module.module_name}
            </span>
            {!readOnly && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() =>
                    handleModuleSelectAll(module.module_code, true)
                  }
                  className="text-sm text-brand-600 hover:text-brand-700 px-2 py-1 rounded hover:bg-brand-50"
                >
                  Grant All
                </button>
                <span className="text-subtext-color">|</span>
                <button
                  type="button"
                  onClick={() =>
                    handleModuleSelectAll(module.module_code, false)
                  }
                  className="text-sm text-brand-600 hover:text-brand-700 px-2 py-1 rounded hover:bg-brand-50"
                >
                  Deny All
                </button>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-2">
            {module.actions.map((action) => {
              const hasPermission = getPermission(
                module.module_code,
                action.action_code
              );
              const requiresReview = getReviewPolicy(
                module.module_code,
                action.action_code
              );

              return (
                <div
                  key={action.action_code}
                  className="flex items-center justify-between gap-3 p-2 rounded hover:bg-neutral-50"
                >
                  <div className="flex items-center gap-2 flex-1">
                    <Checkbox
                      checked={hasPermission}
                      onCheckedChange={(checked) => {
                        if (!readOnly) {
                          onPermissionChange(
                            module.module_code,
                            action.action_code,
                            checked
                          );
                          // If unchecking permission, also uncheck review policy
                          if (!checked) {
                            onReviewPolicyChange(
                              module.module_code,
                              action.action_code,
                              false
                            );
                          }
                        }
                      }}
                      disabled={readOnly}
                      label={action.action_name}
                    />
                  </div>

                  {/* Review Required Checkbox - only show if permission is granted and manager is required */}
                  {hasPermission && isManagerRequired && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-subtext-color">
                        Requires Review:
                      </span>
                      <Checkbox
                        checked={requiresReview}
                        onCheckedChange={(checked) => {
                          if (!readOnly) {
                            onReviewPolicyChange(
                              module.module_code,
                              action.action_code,
                              checked
                            );
                          }
                        }}
                        disabled={readOnly || !hasPermission}
                        label="Yes"
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};
